import * as React from "react"
import { useQueries } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { Info, SlidersHorizontal } from "lucide-react"

import { Chart } from "@/components/charts/Chart"
import { Button } from "@/components/ui/button"
import { DataState } from "@/components/DataState"
import { MetricCard, MetricRow } from "@/components/MetricCard"
import { WorkPanel } from "@/components/WorkPanel"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuth } from "@/lib/auth"
import { formatAmount, formatPercent } from "@/lib/format"
import { agrupar, bucketsDe, ultimoBucketReal } from "@/lib/periodo"
import { cn } from "@/lib/utils"

import { LeyendaDelCorte } from "@/components/CorteReal"
import { useFinanceFilters } from "@/routes/finance/context"
import { nombreCuenta, nombrePais, serieMensual } from "@/routes/finance/mapping"
import { fetchProyeccion, type IdEscenario } from "@/routes/forecast/api"
import {
  AjustarProyeccionPanel,
  CajaProyectadaPanel,
  CobranzaProyectadaPanel,
} from "@/routes/forecast/PanelesProyectados"
import { SupuestosDialog } from "@/routes/forecast/SupuestosDialog"
import { t } from "@/lib/i18n"

const ESCENARIOS: IdEscenario[] = ["pesimista", "moderado", "optimista"]

/**
 * FORECAST: los tres escenarios.
 *
 * Punto 7 de Cesar: «deberíamos de poder tener forecast (tal vez dentro de
 * finanzas) y luego un forecast en base a eso para contabilidad, para
 * adelantarnos a impuestos».
 *
 * LOS IMPUESTOS YA NO ESTÁN AQUÍ. Había un panel «Impuestos que vienen» que
 * estimaba los impuestos aplicando tasas al P&L del escenario, y en Holding ›
 * Obligaciones otro —«Pago de impuestos», punto 13— que sale de la CAJA, con lo
 * pagado y lo proyectado por mes, trimestre y año. Dos respuestas distintas a
 * «¿cuánto viene de impuestos?» sin nada que las uniera: la pantalla se veía
 * bien y el número dependía de por dónde entraras. Queda la de Holding, que es
 * la que contesta con dinero. Aquí sólo se señala.
 *
 * ---------------------------------------------------------------------------
 * LOS TRES A LA VEZ, NO UNO CON UN SELECTOR
 *
 * Un selector de escenario obliga a mirar tres veces y recordar dos cifras.
 * Todo el valor de tener tres escenarios está en la DISTANCIA entre ellos —«si
 * vendes un 20% menos, ¿te alcanza la caja?»— y una distancia no se lee en
 * secuencia. Van los tres, uno al lado del otro.
 *
 * ---------------------------------------------------------------------------
 * LO PRIMERO QUE DICE LA PANTALLA ES CUÁNTO SE PUEDE MOVER
 *
 * Con los datos de este cliente los tres escenarios dan 461k, 465k y 469k: un
 * ±1%. No es un fallo de la fórmula — es que su hoja **apenas proyecta los
 * meses que quedan**: de USD 465.436 del año, sólo 16.108 están por delante del
 * corte. Sin decirlo, cualquiera mira tres cifras casi idénticas y concluye que
 * los escenarios no sirven; diciéndolo, la conclusión es la correcta: que la
 * proyección está vacía y hay que llenarla.
 */
export function ForecastPage() {
  const { activeTenantId } = useAuth()
  const { anio, pais, paisQuery, granularidad } = useFinanceFilters()

  const [supuestosAbiertos, setSupuestosAbiertos] = React.useState(false)
  /*
    QUÉ ESCENARIO SE ESTÁ AJUSTANDO. Los tres se miran a la vez —el valor está
    en la distancia entre ellos— pero una celda se pisa de una en una, y la
    rejilla editable tiene que decir de cuál.

    Arranca en el MODERADO porque es la propia proyección del cliente: es el
    escenario cuyos números él reconoce, y por lo tanto el único en el que
    sabe si una celda está mal.
  */
  const [ajustando, setAjustando] = React.useState<IdEscenario>("moderado")

  const consultas = useQueries({
    queries: ESCENARIOS.map((escenario) => ({
      queryKey: ["forecast-proyeccion", activeTenantId, anio, pais, escenario],
      queryFn: () => fetchProyeccion(anio, escenario, paisQuery),
      enabled: Boolean(activeTenantId),
    })),
  })

  const [pesimista, moderado, optimista] = consultas
  const cargando = consultas.some((c) => c.isLoading)
  const error = consultas.find((c) => c.error)?.error
  const base = moderado.data

  const buckets = bucketsDe(granularidad, anio)
  const corte = base?.corte
  const bucketReal = ultimoBucketReal(buckets, corte?.mes)
  const alcance = base?.alcance

  return (
    <div className="space-y-4">
      <MetricRow className="lg:grid-cols-3">
        {ESCENARIOS.map((id, i) => {
          const d = consultas[i].data
          return (
            <MetricCard
              key={id}
              label={`EBITDA ${anio} · ${d?.escenario.nombre ?? id}`}
              value={d?.subtotales.ebitda.total ?? 0}
              share={d?.indicadores.ingresos}
              loading={cargando}
              /*
                EL ÚNICO ROJO ES EL PESIMISTA, y sólo si da negativo. Lámina 18:
                una cifra en rojo por vista, la que requiere atención. Un EBITDA
                pesimista bajo pero positivo no requiere nada; uno negativo sí.
              */
              tono={id === "pesimista" && (d?.subtotales.ebitda.total ?? 0) < 0
                ? "alerta" : id === "moderado" ? "destacado" : "neutro"}
            />
          )
        })}
      </MetricRow>

      {alcance?.aviso ? (
        <div className="marty-nota flex items-start gap-2 text-xs leading-5" role="status">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <p className="min-w-0 flex-1">{alcance.aviso}</p>
        </div>
      ) : null}

      <WorkPanel
        title={`Los tres escenarios · ${nombrePais(pais || "CONSOLIDADO")}`}
        actions={
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setSupuestosAbiertos(true)}
          >
            <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            {t("Supuestos")}
          </Button>
        }
        meta={
          alcance ? (
            <span className="text-2xs text-muted-foreground">
              {alcance.meses_reales}{" "}{t("mes(es) reales ·")}{" "}{alcance.meses_proyectados}{" "}{t("proyectados ·")}{" "}
              {formatAmount(alcance.ingreso_proyectado)}{" "}{t("por delante del corte")}</span>
          ) : null
        }
        footnote={base?.escenario.nota}
      >
        <DataState
          loading={cargando}
          error={error}
          empty={!cargando && !base?.cuentas.length}
          emptyMessage={`No hay modelo financiero para ${anio}.`}
          onRetry={() => consultas.forEach((c) => void c.refetch())}
          rows={6}
        >
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[16rem]">{t("Concepto")}</TableHead>
                  {ESCENARIOS.map((id) => (
                    <TableHead key={id} className="w-32 text-right">
                      {id === "pesimista" ? "Pesimista"
                        : id === "moderado" ? "Moderado (tu hoja)" : "Optimista"}
                    </TableHead>
                  ))}
                  <TableHead className="w-28 text-right">{t("Distancia")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(base?.cuentas ?? []).map((c) => (
                  <FilaDeCuenta
                    key={c.account}
                    nombre={nombreCuenta(c.account)}
                    valores={consultas.map((q) =>
                      q.data?.cuentas.find((x) => x.account === c.account)?.total ?? 0)}
                    /*
                      LA ELASTICIDAD, A LA VISTA. Una cuenta que no se mueve
                      entre escenarios no es un error de cálculo: es una cuenta
                      FIJA, y decirlo evita la pregunta de por qué la planilla
                      es igual en los tres.
                    */
                    fija={(c as { elasticidad?: number }).elasticidad === 0}
                  />
                ))}
                <FilaDeCuenta
                  nombre="Margen bruto"
                  valores={consultas.map((q) => q.data?.subtotales.margen_bruto.total ?? 0)}
                  subtotal
                />
                <FilaDeCuenta
                  nombre="EBITDA"
                  valores={consultas.map((q) => q.data?.subtotales.ebitda.total ?? 0)}
                  subtotal
                  destacar
                />
              </TableBody>
            </Table>
          </div>

          {base ? (
            <div className="space-y-1.5 border-t px-2.5 py-2">
              {consultas.map((q, i) => (
                <p key={ESCENARIOS[i]} className="text-2xs leading-4 text-muted-foreground">
                  <span className="font-medium text-foreground">
                    {q.data?.escenario.nombre ?? ESCENARIOS[i]}:
                  </span>{" "}
                  {q.data?.escenario.supuestos}
                </p>
              ))}
            </div>
          ) : null}
        </DataState>
      </WorkPanel>

      <WorkPanel
        title={`Ventas mes a mes · los tres escenarios`}
        meta={<span className="text-2xs text-muted-foreground">{anio}{" "}{t("· USD")}</span>}
        footnote={t("Antes del corte los tres coinciden: lo que ya tiene asientos en el libro no se escala. La distancia entre las líneas empieza justo donde empieza la proyección.")}
      >
        <div className="p-2.5">
          {/*
            `corte={bucketReal}`: se calculaba y sólo se usaba para la leyenda de
            abajo. La leyenda DICE dónde está el corte y el dibujo no lo
            enseñaba, que es la mitad que importa — ver el punto 5 de la cuarta
            tanda. Va en buckets y no en meses porque la tabla puede estar por
            trimestres: `ultimoBucketReal` ya resuelve que un trimestre con un
            mes sin respaldo cae del lado del forecast.
          */}
          <Chart
            etiquetas={buckets.map((b) => b.label)}
            corte={bucketReal}
            series={consultas.map((q, i) => ({
              nombre: q.data?.escenario.nombre ?? ESCENARIOS[i],
              valores: agrupar(
                serieMensual(q.data?.cuentas.find((c) => c.account === "Revenue")?.meses),
                granularidad, anio,
              ).map((b) => b.valor),
            }))}
          />
          <LeyendaDelCorte corte={corte} bucketReal={bucketReal} className="pt-1" />
        </div>
      </WorkPanel>

      {/*
        EL ÚNICO SELECTOR DE LA PANTALLA, y gobierna las tres tablas de abajo.

        Arriba no hace falta: los tres escenarios se ven a la vez porque el
        valor está en la distancia entre ellos. Abajo sí, y por una razón
        distinta en cada tabla: la caja de tres escenarios serían 36 columnas
        ilegibles, y una celda se pisa de una en una — hay que decir en cuál.

        Va aquí arriba y no dentro de cada panel para que no haya tres mandos
        para la misma pregunta. Tres selectores desincronizados enseñarían la
        caja del pesimista al lado de los cobros del optimista, cada tabla
        correcta y la lectura conjunta inventada.
      */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-2xs text-muted-foreground">
          {t("Caja, cobros y ajustes según el escenario")}
        </span>
        <div className="flex items-center rounded-md border bg-surface p-0.5">
          {ESCENARIOS.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => setAjustando(id)}
              aria-pressed={ajustando === id}
              className={cn(
                "h-6 rounded-sm px-2 text-2xs font-medium transition-colors",
                ajustando === id
                  ? "bg-surface-sunken text-foreground shadow-xs"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {id === "pesimista" ? t("Pesimista")
                : id === "moderado" ? t("Moderado") : t("Optimista")}
            </button>
          ))}
        </div>
      </div>

      {/*
        LO QUE FALTABA, Y ES LA MITAD DEL PUNTO 6. Cesar: «parece que sólo has
        proyectado ingresos y eso no está bien». El P&L dice cuánto se factura;
        la caja dice si alcanza y los cobros dicen cuándo entra.

        VAN EN ESTE ORDEN a propósito: P&L, caja, cobros, impuestos. Es el
        orden en que una cifra depende de la anterior, y el mismo en que se
        mira cuando el mes viene apretado.
      */}
      <CajaProyectadaPanel
        anio={anio}
        escenario={ajustando}
        pais={paisQuery}
        ajustable={Boolean(base?.ajustable)}
      />

      <CobranzaProyectadaPanel anio={anio} escenario={ajustando} pais={paisQuery} />

      <AjustarProyeccionPanel
        anio={anio}
        pais={paisQuery}
        base={consultas[ESCENARIOS.indexOf(ajustando)]?.data}
        escenario={ajustando}
        ajustable={Boolean(base?.ajustable)}
        noAjustablePorque={base?.no_ajustable_porque ?? null}
      />

      {/* Ver el comentario de cabecera: los impuestos viven en Holding. */}
      <p className="text-2xs leading-4 text-muted-foreground">
        {t("Los impuestos que vienen —pagados y por pagar, por mes, trimestre y año— están en")}{" "}
        <Link to="/holding/obligaciones" className="underline underline-offset-2 hover:text-foreground">
          {t("Holding › Obligaciones")}
        </Link>.
      </p>

      <SupuestosDialog
        anio={anio}
        open={supuestosAbiertos}
        onOpenChange={setSupuestosAbiertos}
      />
    </div>
  )
}

function FilaDeCuenta({
  nombre, valores, subtotal, destacar, fija,
}: {
  nombre: string
  valores: number[]
  subtotal?: boolean
  destacar?: boolean
  fija?: boolean
}) {
  const [malo, , bueno] = valores
  const distancia = bueno - malo
  return (
    <TableRow className={cn(
      subtotal && "font-semibold",
      destacar ? "bg-brand-soft" : subtotal ? "bg-surface-sunken/60" : undefined,
    )}>
      <TableCell>
        {nombre}
        {fija ? (
          <Badge
            variant="outline"
            className="ml-1.5"
            title={t("Cuenta FIJA: no se mueve con las ventas. Un escenario donde la planilla baja con las ventas nunca da miedo, y por eso no sirve.")}
          >
            fija
          </Badge>
        ) : null}
      </TableCell>
      {valores.map((v, i) => (
        <TableCell key={i} className={cn("num", v < 0 && "text-destructive")}>
          {formatAmount(v)}
        </TableCell>
      ))}
      <TableCell className="num text-muted-foreground">
        {distancia === 0 ? "" : formatAmount(distancia)}
      </TableCell>
    </TableRow>
  )
}
